import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i3 from '@angular/common';
import { CommonModule, NgClass } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, HostListener, HostBinding, ViewChild, Output, Input, ChangeDetectionStrategy, ViewEncapsulation, Component, forwardRef, ContentChildren, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { noop, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import * as i2 from '@windmill/ng-windmill/shared';
import { CentricFirstLetterUppercasePipe } from '@windmill/ng-windmill/shared';
import * as i1 from '@angular/cdk/collections';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';

let nextId = 0;
/**
 * To use this component in a module, you can import the `CentricRadioModule`:
 * @example
 * ```typescript
 * import { CentricRadioModule } from '@windmill/ng-windmill/radio';
 * ```
 *
 * To use the standalone component directly, you can import `CentricRadioButtonComponent`:
 * @example
 * ```typescript
 * import { CentricRadioButtonComponent } from '@windmill/ng-windmill/radio';
 * ```
 */
class CentricRadioButtonComponent {
    changeDetector;
    radioDispatcher;
    uppercasePipe;
    /**
     * The id for radio input
     */
    id;
    /**
     * The data-testid attribute for component
     */
    testRadioId;
    /**
     * The name attribute for component
     */
    name = `name-${(nextId += 1)}`;
    /**
     * The label text for component
     */
    label;
    /**
     * If this is true, the component can receive a custom label given as content
     */
    hasCustomLabel = false;
    /**
     * The title attribute for component
     */
    title;
    /**
     * The readonly attribute, if this is true the component will be readonly
     */
    readonly = false;
    /**
     * The input value, if the component is readonly and checked
     */
    readonlyCheckedLabel;
    /**
     * The input size, if the component is readonly
     */
    readonlyInputSize;
    /**
     * The aria-label attribute for component
     */
    ariaLabel;
    /**
     * The aria-labelledby attribute for component
     */
    ariaLabelledby;
    /**
     * The aria-description attribute for component
     */
    ariaDescription;
    /**
     * The aria-describedby attribute for component
     */
    ariaDescribedby;
    /**
     * Checks a radio from a group
     */
    get checked() {
        return this.isChecked || this.radioGroupChecked;
    }
    set checked(value) {
        const newCheckedState = coerceBooleanProperty(value);
        if (this.isChecked !== newCheckedState) {
            this.isChecked = newCheckedState;
            if (newCheckedState) {
                this.radioDispatcher.notify(this.idFormComponent, this.name);
            }
            this.changeDetector.markForCheck();
        }
    }
    /**
     * The value of the component
     */
    get value() {
        return this._value;
    }
    set value(value) {
        if (this._value !== value) {
            this._value = value;
        }
    }
    /**
     * The disabled attribute, if this is true the component will be disabled
     */
    get disabled() {
        return this._disabled || this.radioGroupDisabled;
    }
    set disabled(value) {
        const newDisabledState = coerceBooleanProperty(value);
        if (this._disabled !== newDisabledState) {
            this._disabled = newDisabledState;
            this.changeDetector.markForCheck();
        }
    }
    /**
     *  The required attribute for component
     */
    get required() {
        return this._required;
    }
    set required(value) {
        this._required = coerceBooleanProperty(value);
    }
    /**
     * Custom CSS background color (e.g. hex, rgba, CSS variable etc.) for the radio in an editable state
     */
    backgroundColor;
    /**
     * Event that emits when the value changes
     */
    radioChange = new EventEmitter();
    inputElement;
    inputReadonlyElement;
    class = 'centric-mat-radio-button';
    tabIndexAttribute = '-1';
    idFormComponent = `centric-form-component-${(nextId += 1)}`;
    radioGroupDisabled = false;
    radioGroupChecked = false;
    isChecked = false;
    hasGroup = false;
    _disabled = false;
    _required = false;
    _value = null;
    get hasLabel() {
        return Boolean(this.label || this.hasCustomLabel);
    }
    get readonlyValue() {
        if (this.checked) {
            if (this.label && !this.hasCustomLabel) {
                return this.label;
            }
            if (this.readonlyCheckedLabel) {
                return this.readonlyCheckedLabel;
            }
            return this.uppercasePipe.transform(String(this.checked));
        }
        return null;
    }
    get readonlyPlaceholder() {
        return this.checked ? null : '-';
    }
    get ariaLabelValue() {
        return this.ariaLabel ? this.ariaLabel : null;
    }
    get ariaLabelledbyValue() {
        return this.ariaLabelledby ? this.ariaLabelledby : null;
    }
    get ariaDescriptionValue() {
        return this.ariaDescription ? this.ariaDescription : null;
    }
    get ariaDescribedbyValue() {
        return this.ariaDescribedby ? this.ariaDescribedby : null;
    }
    get backgroundColorValue() {
        return this.readonly || this.disabled || this.checked ? null : this.backgroundColor;
    }
    constructor(changeDetector, radioDispatcher, uppercasePipe) {
        this.changeDetector = changeDetector;
        this.radioDispatcher = radioDispatcher;
        this.uppercasePipe = uppercasePipe;
        this._removeUniqueSelectionListener = radioDispatcher.listen((id, name) => {
            if (id !== this.idFormComponent && name === this.name) {
                this.checked = false;
            }
        });
    }
    ngOnDestroy() {
        this._removeUniqueSelectionListener();
    }
    onKeyDown(event) {
        if (event.key === 'Enter' && event.target.nodeName === 'INPUT') {
            event.preventDefault();
        }
    }
    markForCheck() {
        this.changeDetector.markForCheck();
    }
    onInputChange(event) {
        event.stopPropagation();
        if (!this.hasGroup) {
            this.checked = true;
        }
        this.emitChangeEvent();
    }
    _removeUniqueSelectionListener = () => noop;
    emitChangeEvent() {
        this.radioChange.emit(this._value);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricRadioButtonComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i1.UniqueSelectionDispatcher }, { token: i2.CentricFirstLetterUppercasePipe }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: CentricRadioButtonComponent, isStandalone: true, selector: "centric-radio", inputs: { id: "id", testRadioId: "testRadioId", name: "name", label: "label", hasCustomLabel: "hasCustomLabel", title: "title", readonly: "readonly", readonlyCheckedLabel: "readonlyCheckedLabel", readonlyInputSize: "readonlyInputSize", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby", ariaDescription: "ariaDescription", ariaDescribedby: "ariaDescribedby", checked: "checked", value: "value", disabled: "disabled", required: "required", backgroundColor: "backgroundColor" }, outputs: { radioChange: "radioChange" }, host: { listeners: { "keydown": "onKeyDown($event)" }, properties: { "class.is-readonly": "this.readonly", "class.mat-radio-checked": "this.checked", "class.mat-radio-disabled": "this.disabled", "class": "this.class", "attr.tabindex": "this.tabIndexAttribute" } }, providers: [CentricFirstLetterUppercasePipe], viewQueries: [{ propertyName: "inputElement", first: true, predicate: ["input"], descendants: true }, { propertyName: "inputReadonlyElement", first: true, predicate: ["inputReadonly"], descendants: true }], exportAs: ["centricMatRadio2"], ngImport: i0, template: "@if (readonly) {\r\n  <div\r\n    class=\"readonly-radio windmill-input is-readonly\"\r\n    [class.has-value]=\"readonlyValue\"\r\n    [ngClass]=\"readonlyInputSize\">\r\n    <input\r\n      #inputReadonly\r\n      class=\"input-element\"\r\n      type=\"text\"\r\n      [attr.readonly]=\"readonly\"\r\n      [attr.value]=\"readonlyValue\"\r\n      [attr.placeholder]=\"readonlyPlaceholder\"\r\n      [attr.title]=\"title\"\r\n      [attr.id]=\"id\"\r\n      [attr.data-testid]=\"testRadioId\"\r\n      [attr.name]=\"name\"\r\n      [attr.aria-label]=\"ariaLabelValue\"\r\n      [attr.aria-labelledby]=\"ariaLabelledbyValue\"\r\n      [attr.aria-description]=\"ariaDescriptionValue\"\r\n      [attr.aria-describedby]=\"ariaDescribedbyValue\" />\r\n  </div>\r\n} @else {\r\n  <div class=\"mat-radio-label\">\r\n    <input\r\n      #input\r\n      class=\"mat-radio-input cdk-visually-hidden\"\r\n      type=\"radio\"\r\n      [attr.title]=\"title\"\r\n      [attr.id]=\"id\"\r\n      [attr.data-testid]=\"testRadioId\"\r\n      [checked]=\"checked\"\r\n      [disabled]=\"disabled\"\r\n      [attr.name]=\"name\"\r\n      [attr.value]=\"value\"\r\n      [required]=\"required\"\r\n      [attr.aria-label]=\"ariaLabelValue\"\r\n      [attr.aria-labelledby]=\"ariaLabelledbyValue\"\r\n      [attr.aria-description]=\"ariaDescriptionValue\"\r\n      [attr.aria-describedby]=\"ariaDescribedbyValue\"\r\n      (change)=\"onInputChange($event)\" />\r\n    <div\r\n      class=\"mat-radio-container\"\r\n      [class.has-label]=\"hasLabel\">\r\n      <div\r\n        class=\"mat-radio-outer-circle\"\r\n        [style.backgroundColor]=\"backgroundColorValue\"></div>\r\n      <div class=\"mat-radio-inner-circle\"></div>\r\n    </div>\r\n    @if (hasLabel) {\r\n      <label\r\n        class=\"mat-radio-label-content\"\r\n        [attr.for]=\"id\">\r\n        @if (hasCustomLabel) {\r\n          <ng-content></ng-content>\r\n        } @else {\r\n          {{ label }}\r\n        }\r\n      </label>\r\n    }\r\n  </div>\r\n}\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: FormsModule }, { kind: "ngmodule", type: ReactiveFormsModule }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricRadioButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-radio', exportAs: 'centricMatRadio2', encapsulation: ViewEncapsulation.None, imports: [CommonModule, FormsModule, NgClass, ReactiveFormsModule], providers: [CentricFirstLetterUppercasePipe], changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "@if (readonly) {\r\n  <div\r\n    class=\"readonly-radio windmill-input is-readonly\"\r\n    [class.has-value]=\"readonlyValue\"\r\n    [ngClass]=\"readonlyInputSize\">\r\n    <input\r\n      #inputReadonly\r\n      class=\"input-element\"\r\n      type=\"text\"\r\n      [attr.readonly]=\"readonly\"\r\n      [attr.value]=\"readonlyValue\"\r\n      [attr.placeholder]=\"readonlyPlaceholder\"\r\n      [attr.title]=\"title\"\r\n      [attr.id]=\"id\"\r\n      [attr.data-testid]=\"testRadioId\"\r\n      [attr.name]=\"name\"\r\n      [attr.aria-label]=\"ariaLabelValue\"\r\n      [attr.aria-labelledby]=\"ariaLabelledbyValue\"\r\n      [attr.aria-description]=\"ariaDescriptionValue\"\r\n      [attr.aria-describedby]=\"ariaDescribedbyValue\" />\r\n  </div>\r\n} @else {\r\n  <div class=\"mat-radio-label\">\r\n    <input\r\n      #input\r\n      class=\"mat-radio-input cdk-visually-hidden\"\r\n      type=\"radio\"\r\n      [attr.title]=\"title\"\r\n      [attr.id]=\"id\"\r\n      [attr.data-testid]=\"testRadioId\"\r\n      [checked]=\"checked\"\r\n      [disabled]=\"disabled\"\r\n      [attr.name]=\"name\"\r\n      [attr.value]=\"value\"\r\n      [required]=\"required\"\r\n      [attr.aria-label]=\"ariaLabelValue\"\r\n      [attr.aria-labelledby]=\"ariaLabelledbyValue\"\r\n      [attr.aria-description]=\"ariaDescriptionValue\"\r\n      [attr.aria-describedby]=\"ariaDescribedbyValue\"\r\n      (change)=\"onInputChange($event)\" />\r\n    <div\r\n      class=\"mat-radio-container\"\r\n      [class.has-label]=\"hasLabel\">\r\n      <div\r\n        class=\"mat-radio-outer-circle\"\r\n        [style.backgroundColor]=\"backgroundColorValue\"></div>\r\n      <div class=\"mat-radio-inner-circle\"></div>\r\n    </div>\r\n    @if (hasLabel) {\r\n      <label\r\n        class=\"mat-radio-label-content\"\r\n        [attr.for]=\"id\">\r\n        @if (hasCustomLabel) {\r\n          <ng-content></ng-content>\r\n        } @else {\r\n          {{ label }}\r\n        }\r\n      </label>\r\n    }\r\n  </div>\r\n}\r\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i1.UniqueSelectionDispatcher }, { type: i2.CentricFirstLetterUppercasePipe }], propDecorators: { id: [{
                type: Input
            }], testRadioId: [{
                type: Input
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }], hasCustomLabel: [{
                type: Input
            }], title: [{
                type: Input
            }], readonly: [{
                type: HostBinding,
                args: ['class.is-readonly']
            }, {
                type: Input
            }], readonlyCheckedLabel: [{
                type: Input
            }], readonlyInputSize: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], checked: [{
                type: HostBinding,
                args: ['class.mat-radio-checked']
            }, {
                type: Input
            }], value: [{
                type: Input
            }], disabled: [{
                type: HostBinding,
                args: ['class.mat-radio-disabled']
            }, {
                type: Input
            }], required: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], radioChange: [{
                type: Output
            }], inputElement: [{
                type: ViewChild,
                args: ['input']
            }], inputReadonlyElement: [{
                type: ViewChild,
                args: ['inputReadonly']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }], tabIndexAttribute: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

let nextUniqueId = -1;
const CENTRIC_RADIO_GROUP_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CentricRadioGroupComponent),
    multi: true
};
/**
 * To use this component in a module, you can import the `CentricRadioModule`:
 * @example
 * ```typescript
 * import { CentricRadioModule } from '@windmill/ng-windmill/radio';
 * ```
 *
 * To use the standalone component directly, you can import `CentricRadioGroupComponent`:
 * @example
 * ```typescript
 * import { CentricRadioGroupComponent } from '@windmill/ng-windmill/radio';
 * ```
 */
class CentricRadioGroupComponent {
    changeDetector;
    /**
     * If it is true, the group will have the radios inline
     */
    inline = true;
    /**
     * The aria-label attribute for component
     */
    get ariaLabel() {
        return this._ariaLabel;
    }
    set ariaLabel(newValue) {
        this._ariaLabel = newValue;
        this.ariaLabelGroup = this.readonly ? null : newValue;
    }
    /**
     * The aria-labelledby attribute for component
     */
    get ariaLabelledby() {
        return this._ariaLabelledby;
    }
    set ariaLabelledby(newValue) {
        this._ariaLabelledby = newValue;
        this.ariaLabelledbyGroup = this.readonly ? null : newValue;
    }
    /**
     * The aria-description attribute for component
     */
    get ariaDescription() {
        return this._ariaDescription;
    }
    set ariaDescription(newValue) {
        this._ariaDescription = newValue;
        this.ariaDescriptionGroup = this.readonly ? null : newValue;
    }
    /**
     * The aria-describedby attribute for component
     */
    get ariaDescribedby() {
        return this._ariaDescribedby;
    }
    set ariaDescribedby(newValue) {
        this._ariaDescribedby = newValue;
        this.ariaDescribedbyGroup = this.readonly ? null : newValue;
    }
    /**
     * Whether the group is readonly
     */
    get readonly() {
        return this._readonly;
    }
    set readonly(newValue) {
        this._readonly = newValue;
        this.ariaLabelGroup = newValue ? null : this.ariaLabel;
        this.ariaLabelledbyGroup = newValue ? null : this.ariaLabelledby;
        this.ariaDescriptionGroup = newValue ? null : this.ariaDescription;
        this.ariaDescribedbyGroup = newValue ? null : this.ariaDescribedby;
    }
    /**
     * Sets the size for the input when the component is readonly
     */
    readonlyInputSize;
    /**
     * The id for the readonly input
     */
    readonlyInputId;
    /**
     * Data-testid attribute for the readonly input
     */
    readonlyTestInputId;
    /**
     * The name of the group
     */
    get name() {
        return this._name;
    }
    set name(value) {
        this._name = value;
        this.updateRadioButtonNames();
    }
    /**
     * The value of the group
     */
    get value() {
        return this._value;
    }
    set value(newValue) {
        if (this._value !== newValue) {
            this._value = newValue;
            this.updateSelectedRadioFromValue();
            this.checkSelectedRadioButton();
        }
    }
    /**
     * The selected radio from the group
     */
    get selected() {
        return this._selected;
    }
    set selected(selected) {
        this._selected = selected;
        this.value = selected ? selected.value : null;
        this.checkSelectedRadioButton();
    }
    /**
     * The disabled attribute, if this is true the group will be disabled
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this.updateDisabledRadios();
    }
    /**
     * Event that emits when one of the radios changes
     */
    radioGroupChange = new EventEmitter();
    role = 'radiogroup';
    class = 'centric-mat-radio-group';
    ariaLabelGroup;
    ariaLabelledbyGroup;
    ariaDescriptionGroup;
    ariaDescribedbyGroup;
    radios;
    radioGroupChangeSubscription = Subscription.EMPTY;
    radioChangeSubscription = Subscription.EMPTY;
    _value = null;
    _name = `centric-radio-group-${(nextUniqueId += 1)}`;
    _selected = null;
    _isInitialized = false;
    _disabled = false;
    _readonly = false;
    _ariaLabel;
    _ariaLabelledby;
    _ariaDescription;
    _ariaDescribedby;
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
    }
    ngAfterContentInit() {
        this._isInitialized = true;
        // eslint-disable-next-line no-undefined
        this.radioGroupChangeSubscription = this.radios?.changes.pipe(startWith([undefined])).subscribe(() => {
            if (this.selected) {
                this.writeValue(this.value);
            }
            this.addRadioChangeHandlers();
        });
        setTimeout(() => {
            this.updateDisabledRadios();
            this.updateRadioButtonNames();
            this.updateSelectedRadioFromValue();
        });
    }
    ngOnDestroy() {
        this.radioGroupChangeSubscription.unsubscribe();
        this.radioChangeSubscription.unsubscribe();
    }
    touch() {
        if (this.onTouched) {
            this.onTouched();
        }
    }
    checkSelectedRadioButton() {
        if (this._selected && !this._selected.checked) {
            this._selected.checked = true;
        }
    }
    controlValueAccessorChangeFn = () => noop;
    onTouched = () => noop;
    markRadiosForCheck() {
        if (this.radios) {
            this.radios.forEach(radio => radio.markForCheck());
        }
    }
    emitChangeEvent() {
        if (this._isInitialized) {
            this.radioGroupChange.emit(this._value);
        }
    }
    writeValue(value) {
        this._value = value;
        this.updateCheckedRadio(value);
        this.changeDetector.markForCheck();
    }
    registerOnChange(fn) {
        this.controlValueAccessorChangeFn = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this.changeDetector.markForCheck();
    }
    get readonlyValue() {
        if (this.selectedRadio) {
            return this.selectedRadio.readonlyValue;
        }
        return null;
    }
    get readonlyPlaceholder() {
        if (!this.selectedRadio) {
            return '-';
        }
        return null;
    }
    get selectedRadio() {
        return this.radios.find(radio => radio.checked);
    }
    addRadioChangeHandlers() {
        this.radios?.forEach((radio) => {
            this.radioChangeSubscription = radio.radioChange.subscribe(radioValue => {
                if (!this.value || this.value !== radioValue) {
                    this.controlValueAccessorChangeFn(radioValue);
                    this.writeValue(radioValue);
                    this.touch();
                    this.emitChangeEvent();
                }
                if (this.value === radio.value) {
                    this.selected = radio;
                }
            });
        });
    }
    updateRadioButtonNames() {
        this.radios?.forEach(radio => {
            radio.name = this.name;
            radio.markForCheck();
        });
    }
    updateCheckedRadio(newValue) {
        this.radios?.forEach((radio) => {
            radio.hasGroup = true;
            radio.radioGroupChecked = radio.value === newValue;
            radio.isChecked = false;
            radio.markForCheck();
        });
    }
    updateDisabledRadios() {
        this.radios?.forEach(radio => {
            radio.radioGroupDisabled = this.disabled;
            radio.markForCheck();
        });
    }
    updateSelectedRadioFromValue() {
        const isAlreadySelected = this._selected !== null && this._selected.value === this._value;
        if (this.radios && !isAlreadySelected) {
            this._selected = null;
            this.radios.forEach((radio) => {
                radio.radioGroupChecked = this.value === radio.value;
                radio.hasGroup = true;
                if (radio.checked) {
                    this._selected = radio;
                }
                radio.markForCheck();
            });
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricRadioGroupComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: CentricRadioGroupComponent, isStandalone: true, selector: "centric-radio-group", inputs: { inline: "inline", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby", ariaDescription: "ariaDescription", ariaDescribedby: "ariaDescribedby", readonly: "readonly", readonlyInputSize: "readonlyInputSize", readonlyInputId: "readonlyInputId", readonlyTestInputId: "readonlyTestInputId", name: "name", value: "value", selected: "selected", disabled: "disabled" }, outputs: { radioGroupChange: "radioGroupChange" }, host: { properties: { "class.centric-radio-inline": "this.inline", "attr.role": "this.role", "class": "this.class", "attr.aria-label": "this.ariaLabelGroup", "attr.aria-labelledby": "this.ariaLabelledbyGroup", "attr.aria-description": "this.ariaDescriptionGroup", "attr.aria-describedby": "this.ariaDescribedbyGroup" } }, providers: [CENTRIC_RADIO_GROUP_VALUE_ACCESSOR], queries: [{ propertyName: "radios", predicate: i0.forwardRef(() => CentricRadioButtonComponent), descendants: true }], exportAs: ["centricRadioGroup"], ngImport: i0, template: "@if (readonly) {\r\n  <div\r\n    class=\"windmill-input is-readonly\"\r\n    [class.has-value]=\"selectedRadio\"\r\n    [ngClass]=\"readonlyInputSize\">\r\n    <input\r\n      class=\"input-element\"\r\n      type=\"text\"\r\n      [attr.readonly]=\"readonly\"\r\n      [attr.value]=\"readonlyValue\"\r\n      [attr.placeholder]=\"readonlyPlaceholder\"\r\n      [attr.id]=\"readonlyInputId\"\r\n      [attr.data-testid]=\"readonlyTestInputId\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\" />\r\n  </div>\r\n} @else {\r\n  <ng-content></ng-content>\r\n}\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: FormsModule }, { kind: "ngmodule", type: ReactiveFormsModule }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricRadioGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-radio-group', exportAs: 'centricRadioGroup', providers: [CENTRIC_RADIO_GROUP_VALUE_ACCESSOR], imports: [CommonModule, FormsModule, NgClass, ReactiveFormsModule], standalone: true, template: "@if (readonly) {\r\n  <div\r\n    class=\"windmill-input is-readonly\"\r\n    [class.has-value]=\"selectedRadio\"\r\n    [ngClass]=\"readonlyInputSize\">\r\n    <input\r\n      class=\"input-element\"\r\n      type=\"text\"\r\n      [attr.readonly]=\"readonly\"\r\n      [attr.value]=\"readonlyValue\"\r\n      [attr.placeholder]=\"readonlyPlaceholder\"\r\n      [attr.id]=\"readonlyInputId\"\r\n      [attr.data-testid]=\"readonlyTestInputId\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\" />\r\n  </div>\r\n} @else {\r\n  <ng-content></ng-content>\r\n}\r\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { inline: [{
                type: HostBinding,
                args: ['class.centric-radio-inline']
            }, {
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], readonly: [{
                type: Input
            }], readonlyInputSize: [{
                type: Input
            }], readonlyInputId: [{
                type: Input
            }], readonlyTestInputId: [{
                type: Input
            }], name: [{
                type: Input
            }], value: [{
                type: Input
            }], selected: [{
                type: Input
            }], disabled: [{
                type: Input
            }], radioGroupChange: [{
                type: Output
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }], ariaLabelGroup: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }], ariaLabelledbyGroup: [{
                type: HostBinding,
                args: ['attr.aria-labelledby']
            }], ariaDescriptionGroup: [{
                type: HostBinding,
                args: ['attr.aria-description']
            }], ariaDescribedbyGroup: [{
                type: HostBinding,
                args: ['attr.aria-describedby']
            }], radios: [{
                type: ContentChildren,
                args: [forwardRef(() => CentricRadioButtonComponent), { descendants: true }]
            }] } });

const moduleComponents = [CentricRadioButtonComponent, CentricRadioGroupComponent];
class CentricRadioModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricRadioModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricRadioModule, imports: [CommonModule, FormsModule, ReactiveFormsModule, CentricRadioButtonComponent, CentricRadioGroupComponent], exports: [CentricRadioButtonComponent, CentricRadioGroupComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricRadioModule, providers: [{ provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { float: 'never' } }, CentricFirstLetterUppercasePipe], imports: [CommonModule, FormsModule, ReactiveFormsModule, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricRadioModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule, ReactiveFormsModule, ...moduleComponents],
                    exports: [...moduleComponents],
                    providers: [{ provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { float: 'never' } }, CentricFirstLetterUppercasePipe],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CENTRIC_RADIO_GROUP_VALUE_ACCESSOR, CentricRadioButtonComponent, CentricRadioGroupComponent, CentricRadioModule };
